import { GearCard } from '@/components/GearCard';
import { api } from '@/utils/api';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

interface GolfClub {
  id: number;
  type: string;
  brand: string;
  model: string;
  brandName?: string;
  modelName?: string;
  customModelName?: string;
  loft?: number;
  flex?: string;
  shaftFlex?: string;
  averageDistance?: number;
  maxDistance?: number;
  clubNumber?: number;
  inBag: boolean;
  performance?: {
    averageDistance?: number;
  };
}

interface ClubType {
  key: string;
  label: string;
}

const UserBag: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [clubs, setClubs] = useState<GolfClub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState<string>('');

  // Define club types for grouping
  const clubTypes: ClubType[] = [
    { key: 'driver', label: t('driver') },
    { key: 'wood', label: t('wood') },
    { key: 'hybrid', label: t('hybrid') },
    { key: 'iron', label: t('iron') },
    { key: 'wedge', label: t('wedge') },
    { key: 'putter', label: t('putter') },
  ];

  // Group clubs by type
  const groupedClubs = clubs.reduce((acc: Record<string, GolfClub[]>, club) => {
    if (!acc[club.type]) {
      acc[club.type] = [];
    }
    acc[club.type].push(club);
    return acc;
  }, {});

  // Sort clubs within their groups
  Object.keys(groupedClubs).forEach(type => {
    if (type === 'iron' || type === 'wood' || type === 'hybrid') {
      groupedClubs[type].sort((a, b) => (a.clubNumber || 0) - (b.clubNumber || 0));
    } else if (type === 'wedge') {
      groupedClubs[type].sort((a, b) => (a.loft || 0) - (b.loft || 0));
    }
  });

  // Fetch user's clubs
  useEffect(() => {
    const fetchUserBag = async () => {
      try {
        setLoading(true);
        // Fetch user info to get username
        const userResponse = await api.get(`/users/${userId}`);
        if (userResponse.data && userResponse.data.username) {
          setUsername(userResponse.data.username);
        }

        // Fetch user's clubs
        const response = await api.get(`/golf-gear/users/${userId}/clubs`);
        if (response.data) {
          // Map the API response to match our GolfClub interface
          const formattedClubs = response.data.map((club: any) => ({
            ...club,
            brandName: club.brand || club.brandName,
            modelName: club.model || club.modelName,
            inBag: true, // All clubs in a user's bag are considered "in bag"
            performance: {
              averageDistance: club.averageDistance
            }
          }));
          setClubs(formattedClubs);
        } else {
          setClubs([]);
        }
      } catch (err) {
        console.error('Error fetching user bag:', err);
        setError(t('error_loading_bag'));
      } finally {
        setLoading(false);
      }
    };

    fetchUserBag();
  }, [userId]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/dashboard" />
          </IonButtons>
          <IonTitle>{username ? `${username}'s ${t('bag')}` : t('bag')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <IonSpinner />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-center text-gray-500">{error}</p>
          </div>
        ) : clubs.length === 0 ? (
          <div className="flex flex-col justify-center items-center h-full">
            <p className="text-center text-gray-500">
              {t('user_has_no_clubs', { username })}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-6 max-w-3xl">
              {clubTypes.map((type) => {
                const typeClubs = groupedClubs[type.key] || [];
                if (typeClubs.length === 0) return null;

                return (
                  <div key={type.key} className="mb-4">
                    <h2 className="text-xl font-semibold mb-2">{type.label}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {typeClubs.map((club) => (
                        <GearCard
                          key={club.id}
                          club={club}
                          onClick={() => { }}
                          showDistance={true}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default UserBag;
